/**
 * TemplatePreview Component
 *
 * Displays email template metadata and renders the HTML content in a sandboxed iframe.
 * In edit mode, shows minimal metadata and maximizes editor space.
 */

import { useState, useRef, useEffect } from 'react'
import { motion } from 'motion/react'
import { Mail, Users, Calendar, Tag, Link as LinkIcon, Check, Pencil, Loader2, Send, Trash2 } from 'lucide-react'
import { formatLabel, formatAudience, type EmailTemplate } from '@/lib/email-templates'
import { TemplateEditor } from './TemplateEditor'
import { cn } from '@/lib/utils'

interface TemplatePreviewProps {
  template: EmailTemplate
  onShareLink?: () => void
  copied?: boolean
  // Preview mode props
  previewContent?: string | null  // HTML content from database for preview
  isLoadingPreview?: boolean
  // Edit mode props
  isEditing?: boolean
  onEdit?: () => void
  editContent?: string | null  // HTML content from database for editing
  onSave?: (htmlContent: string) => void
  onCancel?: () => void
  isSaving?: boolean
  // AI editing props
  isAiProcessing?: boolean
  pendingAiHtml?: string | null
  onAiApplied?: (appliedHtml: string) => void
  originalContent?: string | null
  onRevert?: () => void
  // Test email props
  onTestEmail?: () => void
  isSendingTestEmail?: boolean
  // Delete props
  onDelete?: () => void
  // Rename props
  onRename?: (newName: string) => void
}

export function TemplatePreview({
  template,
  onShareLink,
  copied,
  previewContent,
  isLoadingPreview,
  isEditing,
  onEdit,
  editContent,
  onSave,
  onCancel,
  isSaving,
  isAiProcessing,
  pendingAiHtml,
  onAiApplied,
  originalContent,
  onRevert,
  onTestEmail,
  isSendingTestEmail,
  onDelete,
  onRename,
}: TemplatePreviewProps) {
  const [isRenamingTitle, setIsRenamingTitle] = useState(false)
  const [renameValue, setRenameValue] = useState(template.name)
  const renameInputRef = useRef<HTMLInputElement>(null)

  // Reset rename state when template changes
  useEffect(() => {
    setIsRenamingTitle(false)
    setRenameValue(template.name)
  }, [template.id, template.name])

  // Focus input when rename mode activates
  useEffect(() => {
    if (isRenamingTitle && renameInputRef.current) {
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [isRenamingTitle])

  const handleRenameSubmit = () => {
    const trimmed = renameValue.trim()
    if (trimmed && trimmed !== template.name && onRename) {
      onRename(trimmed)
    }
    setIsRenamingTitle(false)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRenameSubmit()
    } else if (e.key === 'Escape') {
      setRenameValue(template.name)
      setIsRenamingTitle(false)
    }
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Metadata Header - Compact when editing */}
      <div className={cn(
        "flex-shrink-0 border-b border-slate-200 bg-white transition-all",
        isEditing ? "px-4 py-2" : "p-4"
      )}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              {isEditing && onRename && isRenamingTitle ? (
                <input
                  ref={renameInputRef}
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={handleRenameSubmit}
                  onKeyDown={handleRenameKeyDown}
                  className="text-base font-semibold text-eventus-navy bg-transparent border-b-2 border-eventus-purple/50 outline-none px-0 py-0.5 min-w-[120px] max-w-[400px]"
                />
              ) : (
                <h2
                  className={cn(
                    "font-semibold text-eventus-navy truncate transition-all",
                    isEditing ? "text-base" : "text-lg",
                    isEditing && onRename && "cursor-pointer hover:text-eventus-purple border-b border-transparent hover:border-eventus-purple/30"
                  )}
                  onClick={() => {
                    if (isEditing && onRename) {
                      setRenameValue(template.name)
                      setIsRenamingTitle(true)
                    }
                  }}
                  title={isEditing && onRename ? "Click to rename" : undefined}
                >
                  {template.name}
                </h2>
              )}

              {/* Edit button - only shown when not editing */}
              {onEdit && !isEditing && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={onEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-eventus-purple hover:bg-eventus-purple/90 text-white text-xs font-medium rounded-lg transition-all shadow-sm"
                  title="Edit template"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </motion.button>
              )}

              {/* Share button */}
              {onShareLink && !isEditing && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={onShareLink}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded-lg transition-all"
                  title="Copy shareable link"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-green-600" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-3.5 h-3.5" />
                      <span>Share</span>
                    </>
                  )}
                </motion.button>
              )}

              {/* Test Email button */}
              {onTestEmail && !isEditing && previewContent && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={onTestEmail}
                  disabled={isSendingTestEmail}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Send test email to yourself"
                >
                  {isSendingTestEmail ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Test Email</span>
                    </>
                  )}
                </motion.button>
              )}

              {/* Delete button */}
              {onDelete && !isEditing && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={onDelete}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-lg transition-all"
                  title="Delete template"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete</span>
                </motion.button>
              )}
            </div>

            {/* Subject line - shown when not editing */}
            {!isEditing && (
              <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <span className="truncate">{template.subject}</span>
              </p>
            )}

            {/* Description - only shown when not editing */}
            {!isEditing && template.description && (
              <p className="text-sm text-slate-500 mt-2">{template.description}</p>
            )}
          </div>
        </div>

        {/* Tags - only shown when not editing */}
        {!isEditing && (
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-eventus-purple/10 text-eventus-purple text-xs font-medium rounded-full">
              <Tag className="w-3 h-3" />
              {formatLabel(template.category)}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-eventus-teal/20 text-eventus-navy text-xs font-medium rounded-full">
              <Users className="w-3 h-3" />
              {formatAudience(template.audience)}
            </span>
            {template.lastModified && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                <Calendar className="w-3 h-3" />
                {template.lastModified}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Iframe Preview OR Editor - Fills remaining space with minimal padding */}
      <div className={cn(
        "flex-1 min-h-0 overflow-hidden",
        isEditing ? "p-0" : "p-3 bg-slate-100"
      )}>
        <motion.div
          key={isEditing ? 'editor' : template.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
          className={cn("h-full rounded-xl", isEditing ? "" : "overflow-hidden")}
        >
          {isEditing && editContent && onSave && onCancel ? (
            <TemplateEditor
              initialContent={editContent}
              onSave={onSave}
              onCancel={onCancel}
              isSaving={isSaving ?? false}
              isAiProcessing={isAiProcessing}
              pendingAiHtml={pendingAiHtml}
              onAiApplied={onAiApplied}
              originalContent={originalContent ?? undefined}
              onRevert={onRevert}
            />
          ) : isLoadingPreview ? (
            <div className="w-full h-full rounded-xl border border-slate-300 overflow-hidden shadow-sm bg-white flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="w-8 h-8 mb-3 animate-spin" />
              <p className="text-sm">Loading preview...</p>
            </div>
          ) : previewContent ? (
            <div className="w-full h-full rounded-xl border border-slate-300 overflow-hidden shadow-sm bg-white">
              <iframe
                srcDoc={previewContent.replace(
                  /(<head[^>]*>)/i,
                  '$1<style>html,body,p,td,th,li,div,span{font-family:Arial,Helvetica,sans-serif}</style>'
                )}
                title={`Preview: ${template.name}`}
                className="w-full h-full border-0"
                sandbox="allow-same-origin"
              />
            </div>
          ) : (
            <div className="w-full h-full rounded-xl border border-slate-300 overflow-hidden shadow-sm bg-white flex flex-col items-center justify-center text-slate-400">
              <Mail className="w-8 h-8 mb-3" />
              <p className="text-sm">No preview available</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}
